// =============================================================================
// useDraggableFloat.ts
// Universal hook: right-click any floating element → snap to corner, free drag,
// or reset position. Position is persisted in localStorage.
// =============================================================================
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

type Corner = 'tl' | 'tr' | 'bl' | 'br';
interface SavedPos { left: number; top: number; }

function cornerPos(corner: Corner): SavedPos {
  const pad = 20;
  const vw  = window.innerWidth;
  const vh  = window.innerHeight;
  if (corner === 'tl') return { left: pad,          top: pad };
  if (corner === 'tr') return { left: vw - 160,     top: pad };
  if (corner === 'bl') return { left: pad,          top: vh - 130 };
  return                       { left: vw - 160,    top: vh - 130 }; // br
}

const MENU_STYLE: React.CSSProperties = {
  position:       'fixed',
  zIndex:          99999,
  background:     'rgba(3,8,20,0.97)',
  border:         '1px solid rgba(37,181,168,0.38)',
  borderRadius:    9,
  padding:        '5px 0',
  minWidth:        158,
  boxShadow:      '0 12px 40px rgba(0,0,0,0.75), 0 0 18px rgba(37,181,168,0.14)',
  backdropFilter: 'blur(18px)',
  userSelect:     'none',
};

function MenuRow({
  label, color, onClick,
}: { label: string; color?: string; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding:    '7px 14px',
        fontSize:    11,
        color:       hov ? '#5eead4' : (color || '#94a3b8'),
        cursor:     'pointer',
        background:  hov ? 'rgba(37,181,168,0.1)' : 'transparent',
        transition: 'all 0.1s',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </div>
  );
}

export function useDraggableFloat(
  storageKey:  string,
  defaultStyle: React.CSSProperties,   // e.g. { position:'fixed', right:20, bottom:20, zIndex:60 }
) {
  // ── Saved position (localStorage) ─────────────────────────────────────────
  const [saved, setSaved] = useState<SavedPos | null>(() => {
    try {
      const raw = localStorage.getItem(`r2_fp_${storageKey}`);
      return raw ? (JSON.parse(raw) as SavedPos) : null;
    } catch { return null; }
  });

  // ── Context menu ───────────────────────────────────────────────────────────
  const [menu, setMenu]     = useState<{ x: number; y: number } | null>(null);
  // ── Free drag ─────────────────────────────────────────────────────────────
  const [freeDrag, setFree] = useState(false);
  // ── Hover hint ────────────────────────────────────────────────────────────
  const [hinted, setHinted] = useState(false);

  const elemRef  = useRef<HTMLElement>(null);
  const dragRef  = useRef<{ sx: number; sy: number; ex: number; ey: number } | null>(null);

  // ── Persist helper ─────────────────────────────────────────────────────────
  const persist = useCallback((p: SavedPos) => {
    setSaved(p);
    localStorage.setItem(`r2_fp_${storageKey}`, JSON.stringify(p));
  }, [storageKey]);

  // ── Computed position style ────────────────────────────────────────────────
  // When we have a saved absolute (left/top) position, override right/bottom/etc.
  const posStyle: React.CSSProperties = saved
    ? { ...defaultStyle, left: saved.left, top: saved.top, right: 'auto', bottom: 'auto' }
    : defaultStyle;

  // ── Context menu – right-click ─────────────────────────────────────────────
  const onContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY });
    setFree(false);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('[data-r2-float-menu]')) setMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [!!menu]);

  // ── Free drag – mouse events ───────────────────────────────────────────────
  const onDragStart = useCallback((e: React.MouseEvent) => {
    if (!freeDrag) return;
    e.preventDefault();
    const el   = elemRef.current;
    const rect = el?.getBoundingClientRect();
    dragRef.current = {
      sx: e.clientX, sy: e.clientY,
      ex: rect?.left ?? 0, ey: rect?.top ?? 0,
    };
  }, [freeDrag]);

  useEffect(() => {
    if (!freeDrag) return;
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const el = elemRef.current;
      const w  = el?.offsetWidth  ?? 80;
      const h  = el?.offsetHeight ?? 80;
      setSaved({
        left: Math.max(0, Math.min(window.innerWidth  - w, dragRef.current.ex + (e.clientX - dragRef.current.sx))),
        top:  Math.max(0, Math.min(window.innerHeight - h, dragRef.current.ey + (e.clientY - dragRef.current.sy))),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      setSaved(prev => {
        if (prev) localStorage.setItem(`r2_fp_${storageKey}`, JSON.stringify(prev));
        return prev;
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  }, [freeDrag, storageKey]);

  // ESC exits drag mode
  useEffect(() => {
    if (!freeDrag) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setFree(false); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [freeDrag]);

  // ── Snap / reset actions ───────────────────────────────────────────────────
  const snapTo = useCallback((c: Corner) => {
    setMenu(null);
    persist(cornerPos(c));
  }, [persist]);

  const resetPos = useCallback(() => {
    setMenu(null);
    setSaved(null);
    localStorage.removeItem(`r2_fp_${storageKey}`);
  }, [storageKey]);

  const enterFreeDrag = useCallback(() => {
    setMenu(null);
    setFree(true);
  }, []);

  // ── Rendered overlays (portal for menu, inline for hints) ─────────────────
  const menuEl = menu ? createPortal(
    <div
      data-r2-float-menu="1"
      style={{
        ...MENU_STYLE,
        left: Math.min(menu.x, window.innerWidth  - 170),
        top:  Math.min(menu.y, window.innerHeight - 185),
      }}
    >
      <div style={{ padding: '3px 12px 5px', fontSize: 8, color: 'rgba(94,234,212,0.55)', fontWeight: 700, letterSpacing: '0.11em' }}>
        R2 FLOAT CONTROL
      </div>
      <MenuRow label="↖ Top-Left"     onClick={() => snapTo('tl')} />
      <MenuRow label="↗ Top-Right"    onClick={() => snapTo('tr')} />
      <MenuRow label="↙ Bottom-Left"  onClick={() => snapTo('bl')} />
      <MenuRow label="↘ Bottom-Right" onClick={() => snapTo('br')} />
      <div style={{ height: 1, background: 'rgba(37,181,168,0.12)', margin: '4px 0' }} />
      <MenuRow label="✦ Free drag"      color="#5eead4" onClick={enterFreeDrag} />
      <MenuRow label="↺ Reset position" color="#64748b" onClick={resetPos} />
    </div>,
    document.body,
  ) : null;

  const hintEl = (hinted && !menu && !freeDrag) ? (
    <div
      aria-hidden="true"
      style={{
        position:     'absolute',
        bottom:       'calc(100% + 5px)',
        left:         '50%',
        transform:    'translateX(-50%)',
        background:   'rgba(0,0,0,0.82)',
        color:        'rgba(148,172,210,0.7)',
        fontSize:      8,
        padding:      '2px 7px',
        borderRadius:  3,
        whiteSpace:   'nowrap',
        pointerEvents:'none',
        zIndex:        1,
      }}
    >
      ⟲ right-click to move
    </div>
  ) : null;

  const dragRingEl = freeDrag ? (
    <div
      aria-hidden="true"
      style={{
        position:     'absolute',
        inset:        -2,
        borderRadius: 'inherit',
        border:       '1.5px dashed rgba(37,181,168,0.7)',
        pointerEvents:'none',
        zIndex:        1,
        animation:    'r2DragRingPulse 1.2s ease-in-out infinite',
      }}
    />
  ) : null;

  return {
    posStyle,
    freeDrag,
    onContextMenu,
    onDragStart,
    onMouseEnter:  () => setHinted(true),
    onMouseLeave:  () => setHinted(false),
    menuEl,        // render this in the component (portal)
    hintEl,        // render inside the element (absolute)
    dragRingEl,    // render inside the element (absolute)
    elemRef,
  } as const;
}
